import { DataTable } from '@/components/ui/data-table/DataTable';
import { ColumnDef } from '@tanstack/react-table';
import dayjs from 'dayjs';
import { RouteParams } from '@/router/type';
import {
  useDownloadFileOnRemote,
  useGetServerlessStorage,
  useGetServerlessStorageSize,
} from '@/service/serverless-gpu/hook';
import { Link, useParams } from 'react-router-dom';
import { CopyIcon, DownloadIcon, FolderIcon, AlertCircle } from 'lucide-react';
import { transformToTreeStructure } from './transformToTreeStructure';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { useGetUserBalance } from '@/service/payment/hook';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { fCurrencyUSDExtended } from '@/utils/formatNumber';
import PATH_PAGE from '@/router/path';
import { useQuickTopup } from '@/hooks/useQuickTopup';
import { QuickTopupDialog } from '@/components/quick-topup/QuickTopupDialog';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { cn } from '@/lib/utils';
import { handleMessageError } from '@/utils';
import { getFileIconInfo } from '@/utils/getFileIconInfo';
import { formatFileSize } from '@/utils/formatNumber';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { MoreVertical, Eye, Trash2 } from 'lucide-react';
import ToolbarActionStorage from './components/ToolbarActionStorage';
import DialogContentDeleteFile from './components/DialogContentDeleteFile';
import { Dialog, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import * as React from 'react';
import { toastSomethingError } from '@/components/ui/toast-content';
import ColumnButtonSort from '@/components/ui/data-table/ColumnButtonSort';
import DialogPreviewFile from './components/DialogPreviewFile';
import DialogCopyFile from './components/DialogCopyFile';

export type StorageItem = {
  name: string;
  size: string;
  type: string;
  modified: string;
  fullPath: string;
};

const StorageDataTable = React.memo(
  ({
    data,
    isLoading,
    columns,
    onClickRow,
  }: {
    data: any[];
    isLoading: boolean;
    columns: ColumnDef<StorageItem>[];
    onClickRow: (row: any) => void;
  }) => {
    return (
      <DataTable
        columns={columns}
        data={data}
        slots={{
          pagination: {
            isSelectPageSize: false,
          },
          cell: {
            className: 'py-4',
          },
        }}
        isHidePagination
        onClickRow={onClickRow}
        isLoading={isLoading}
        defaultSorting={[{ id: 'modified', desc: true }]}
      />
    );
  }
);

StorageDataTable.displayName = 'StorageDataTable';

const ServerlessGPUDetailStorage = () => {
  const { projectId } = useParams<RouteParams>();
  const { data, isLoading, refetch, isFetching } = useGetServerlessStorage({
    project_id: projectId!,
  });
  const { data: storageSize, refetch: refetchStorageSize } =
    useGetServerlessStorageSize({
      project_id: projectId!,
    });
  const [currentPath, setCurrentPath] = useState<string[]>([]);
  const [previewFile, setPreviewFile] = useState<StorageItem>();
  const [openPreviewFile, setOpenPreviewFile] = useState(false);

  // Credit balance check
  const { data: creditData, isLoading: isLoadingCredit } = useGetUserBalance();
  const totalAvailableCredit = creditData?.balance ?? 0;
  const hasInsufficientBalance = totalAvailableCredit < 0.5;
  const { isOpen, setIsOpen, openTopup, onSuccess } = useQuickTopup();

  const treeData = transformToTreeStructure(data?.files || []);

  const getCurrentDirectoryData = () => {
    let current = treeData;
    for (const path of currentPath) {
      const found = current.find((item) => item.name === path);
      if (found?.children) {
        current = found.children;
      } else {
        return [];
      }
    }
    return current;
  };

  const handleExpandRow = (row: any) => {
    const type = row.getValue('type');
    if (type === 'directory') {
      const name = row.getValue('name');
      setCurrentPath((prev) => [...prev, name]);
    }

    if (type === 'regular file') {
      setPreviewFile(row.original);
      setOpenPreviewFile(true);
    }
  };

  const handleNavigateToPath = (index: number) => {
    setCurrentPath((prev) => prev.slice(0, index + 1));
  };

  const tableData = getCurrentDirectoryData().map((item) => ({
    name: item.name,
    size: item.size.toString(),
    type: item.type,
    modified: item.modified,
    fullPath: item.fullPath,
  }));
  // .sort((a, b) => b.modified.localeCompare(a.modified));

  const handleRefresh = async () => {
    await Promise.all([refetch(), refetchStorageSize()]);
  };

  const { mutateAsync: downloadFile } = useDownloadFileOnRemote({
    onSuccess: (data) => {
      try {
        if (data.downloads && data.downloads.length > 0) {
          const download = data.downloads[0];
          window.open(download.url, '_blank');
        }
      } catch (e) {
        toastSomethingError(e);
      }
    },
  });

  const handleDownloadFile = ({
    projectId,
    filePath,
  }: {
    projectId: string;
    filePath: string;
  }) => {
    toast.promise(
      downloadFile({
        project_id: projectId!,
        file_path: [filePath],
      }),
      {
        loading: 'Downloading...',
        success: 'Downloaded successfully',
        error: (error) => handleMessageError(error),
      }
    );
  };

  const handlePreviewFile = (file: StorageItem) => {
    setPreviewFile(file);
    setOpenPreviewFile(true);
  };

  const FileActionsDropdown = React.memo(
    ({
      row,
      projectId,
      onPreview,
      onDownload,
    }: {
      row: any;
      projectId: string;
      onPreview: (file: StorageItem) => void;
      onDownload: (params: { projectId: string; filePath: string }) => void;
    }) => {
      const isFolder = row.getValue('type') === 'directory';
      const fileName = row.getValue('name') as string;
      const filePath = row.original.fullPath;

      const [dialogType, setDialogType] = useState<'copy' | 'delete'>('copy');
      const [open, setOpen] = useState(false);

      return (
        <div onClick={(e) => e.stopPropagation()}>
          <Dialog open={open} onOpenChange={setOpen}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-4 w-4 p-0">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {isFolder ? null : (
                  <>
                    <DropdownMenuItem onClick={() => onPreview(row.original)}>
                      <Eye className="mr-2 h-4 w-4" />
                      <span>Preview</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => onDownload({ projectId, filePath })}
                    >
                      <DownloadIcon className="mr-2 h-4 w-4" />
                      <span>Download</span>
                    </DropdownMenuItem>
                  </>
                )}

                <DialogTrigger asChild onClick={() => setDialogType('copy')}>
                  <DropdownMenuItem>
                    <CopyIcon className="mr-2 h-4 w-4" />
                    <span>Copy to</span>
                  </DropdownMenuItem>
                </DialogTrigger>

                <DropdownMenuSeparator />

                <DialogTrigger asChild onClick={() => setDialogType('delete')}>
                  <DropdownMenuItem className="text-destructive">
                    <Trash2 className="mr-2 h-4 w-4" />
                    <span>Delete</span>
                  </DropdownMenuItem>
                </DialogTrigger>
              </DropdownMenuContent>
            </DropdownMenu>
            {dialogType === 'delete' && (
              <DialogContentDeleteFile path={filePath} projectId={projectId} />
            )}
            {dialogType === 'copy' && (
              <DialogCopyFile projectId={projectId} filePath={filePath} onSuccess={()=> setOpen(false)}/>
            )}
          </Dialog>
        </div>
      );
    }
  );

  FileActionsDropdown.displayName = 'FileActionsDropdown';

  const columns: ColumnDef<StorageItem>[] = [
    {
      accessorKey: 'name',
      header: ({ column }) => <ColumnButtonSort label="Name" column={column} />,
      cell: ({ row }) => {
        const isFolder = row.getValue('type') === 'directory';
        const fileName = row.getValue('name') as string;

        if (isFolder) {
          return (
            <div className="flex items-center gap-2">
              <FolderIcon className="h-5 w-5 text-yellow-400" />
              <span>{fileName}</span>
            </div>
          );
        }

        const { icon: FileTypeIcon, color } = getFileIconInfo(fileName);

        return (
          <div className="flex items-center gap-2">
            <FileTypeIcon className={cn('h-5 w-5', color)} />
            <span>{fileName}</span>
          </div>
        );
      },
      size: 400,
    },
    {
      accessorKey: 'size',
      header: ({ column }) => <ColumnButtonSort label="Size" column={column} />,
      cell: ({ row }) => {
        const size = Number(row.getValue('size'));
        return formatFileSize(size);
      },
      size: 100,
    },
    {
      accessorKey: 'type',
      header: 'Type',
      cell: ({ row }) => {
        const type = row.getValue('type') as string;
        return (
          <span className="text-muted-foreground">
            {type === 'folder' ? 'folder' : type}
          </span>
        );
      },
      size: 100,
    },
    {
      accessorKey: 'modified',
      header: ({ column }) => (
        <ColumnButtonSort label="Modified" column={column} />
      ),
      cell: ({ row }) => {
        const date = dayjs(row.getValue('modified'));
        return (
          <div className="flex flex-col">
            <span>{date.format('DD MMM YYYY HH:mm')}</span>
          </div>
        );
      },
      size: 130,
    },
    {
      id: 'actions',
      cell: ({ row }) => {
        return (
          <FileActionsDropdown
            row={row}
            projectId={projectId!}
            onPreview={handlePreviewFile}
            onDownload={handleDownloadFile}
          />
        );
      },
      size: 50,
    },
  ];

  return (
    <div>
      <div className="container mt-8">
        {/* Insufficient Balance Warning */}
        {!isLoadingCredit && hasInsufficientBalance && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Insufficient Credit Balance</AlertTitle>
            <AlertDescription className="mt-2">
              <p>
                Your current balance is {fCurrencyUSDExtended(totalAvailableCredit)}.
                Please top up your account to upload files.
              </p>
              <Button
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={openTopup}
              >
                Quick Top-up $5
              </Button>
            </AlertDescription>
          </Alert>
        )}
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Size: {formatFileSize(storageSize?.data?.bytes ?? 0)}
          </p>
          <ToolbarActionStorage
            currentPath={currentPath}
            handleRefresh={handleRefresh}
            loadingRefresh={isFetching}
          />
        </div>
        <>
          <div className="mt-2">
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink
                    onClick={() => setCurrentPath([])}
                    className={cn(
                      'flex items-center gap-2',
                      currentPath.length > 0 &&
                        'cursor-pointer transition-colors hover:text-primary'
                    )}
                  >
                    <span>My Storage</span>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                {currentPath.map((path, index) => (
                  <BreadcrumbItem key={index}>
                    <BreadcrumbSeparator />
                    {index === currentPath.length - 1 ? (
                      <BreadcrumbPage>{path}</BreadcrumbPage>
                    ) : (
                      <BreadcrumbLink
                        onClick={() => handleNavigateToPath(index)}
                        className={cn('cursor-pointer transition-colors')}
                      >
                        {path}
                      </BreadcrumbLink>
                    )}
                  </BreadcrumbItem>
                ))}
              </BreadcrumbList>
            </Breadcrumb>
          </div>

          <div className="mt-3">
            <StorageDataTable
              data={tableData}
              isLoading={isLoading}
              columns={columns}
              onClickRow={handleExpandRow}
            />
          </div>
        </>
      </div>
      <DialogPreviewFile
        open={openPreviewFile}
        onOpenChange={(open) => {
          setOpenPreviewFile(open);
        }}
        projectId={projectId!}
        file={previewFile}
      />

      <QuickTopupDialog open={isOpen} onOpenChange={setIsOpen} onSuccess={onSuccess} />
    </div>
  );
};

export default ServerlessGPUDetailStorage;
